import React, { ReactNode } from 'react';
import { Navbar, Container, Nav, NavDropdown } from 'react-bootstrap';
import { Routes, Route, Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import Login from './pages/login';
import { AuthProvider, useAuth } from './auth/auth-context'
import {BsPerson} from 'react-icons/bs'
import { Dashboard } from './pages/dashboard/dashboard';
import UserSettings from './pages/user-settings/user-settings';
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { AppProvider, useAppContext } from './AppContext';
import AlertReport from './pages/reports/alert-report/alert-report';
import SignupForm from './pages/signup';
import SwitchUserDropdown from './SwitchUserDropdown';
const isDev = process.env.NODE_ENV === 'development'


function NoMatch() {
  return (
    <div>
      <h2>Nothing to see here!</h2>
      <p>
        <Link to="/">Go to the home page</Link>
      </p>
    </div>
  )
}

function LegacyPartnershipRedirect({ print = false }: { print?: boolean }) {
  const { id } = useParams()
  const location = useLocation()
  const suffix = id ? `/${id}${print ? '/print' : ''}` : ''
  return <Navigate to={`/partnerships${suffix}${location.search}`} replace />
}

const UserDropdown = ({ username }: { username?: string }) => {
  return (
    <div className="d-flex align-items-center" aria-label={username ? `Current user ${username}` : 'Current user'}>
      <BsPerson className="me-2" style={{ fontSize: '1.2rem' }} />
      {username ? (
        <span className="text-muted small" title={username}>
          {username}
        </span>
      ) : null}
    </div>
  )
}



function MyNavbar() {
  const navigate = useNavigate()
  const { logout, isLoggedIn, user, login } = useAuth()
  const {isPdf} = useAppContext()
  const [showSwitchModal, setShowSwitchModal] = React.useState(false);
  const origin = isDev ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com'
  const img = origin + '/logo' + (user?.username ? '/' + user?.username : '')
  const handleLogout = () => {
    logout()
    navigate('/login')
  }
  const handleSwitchUser = async (username: string) => {
    setShowSwitchModal(false);
    try {
      const res = await fetch(origin + '/user/checkout-as', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('authToken') || ''
        },
        body: JSON.stringify({ username })
      });
      if (!res.ok) throw new Error('Failed to switch user');
      const { token } = await res.json();
      login(token);
      window.location.reload();
    } catch (err) {
      alert('Failed to switch user');
    }
  };
  if(isPdf) return <></>
  return (
    <>
      <Navbar expand="md" className='bg-light-subtle'>
        <Container fluid>
          <Navbar.Brand as={Link} to="/">
            <img
              src={img}
              height="30"
              id="p_home_brand"
              alt="Navbar Brand"
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="navbarNavDropdown" />
          <Navbar.Collapse id="navbarNavDropdown">
            {isLoggedIn ? 
            <Nav className="w-100">
              <div className="ms-md-auto d-flex align-items-center">
                {(user?.isAdmin || user?.admin || user?.switchAccessUsernames?.length || user?.switchedFrom?.username) && (
                  <Nav.Link onClick={() => setShowSwitchModal(true)} id="u_switchuser" className="me-md-2">
                    Switch user
                  </Nav.Link>
                )}
                <NavDropdown
                  title={<UserDropdown username={user?.username} />}
                  id="basic-nav-dropdown"
                  align="end"
                  className="me-md-2 hide-dropdown-arrow"
                >
                  <NavDropdown.Item as={Link} to="/user-settings" id="u_settings">
                    User settings
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}>Logout</NavDropdown.Item>
                </NavDropdown>
              </div>
            </Nav>
            : '' }
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <SwitchUserDropdown
        show={showSwitchModal}
        onHide={() => setShowSwitchModal(false)}
        onSwitch={handleSwitchUser}
        currentUsername={user?.username}
        allowedUsernames={user?.switchAccessUsernames}
        isAdmin={!!user?.isAdmin || !!user?.admin}
        switchedFromUsername={user?.switchedFrom?.username}
      />
    </>
  );
}

const SecuredComponent = (opts: {element: ReactNode } )=> {
  const { isLoggedIn } = useAuth()
  

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return (<>{opts.element}</>)
}

const AdminSecuredComponent = (opts: {element: ReactNode } )=> {
  const { isLoggedIn, user } = useAuth()
  

  if (!isLoggedIn || !user?.isAdmin) {
    return <Navigate to="/login" replace />;
  }
  return (<>{opts.element}</>)
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <AuthProvider>
          <MyNavbar />
            <Routes>
              <Route path="/" element={<SecuredComponent element={<Dashboard />} />}>
                <Route path="/d/:network" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/transaction-report" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/reference-report" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/publisher-report" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/campaign-report" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/click-report" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/feed-management" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/link-script" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/link-script/analysis" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/link-script/growth-opportunities" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/link-script/settings" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/link-script/partnerships" element={<SecuredComponent element={<LegacyPartnershipRedirect />} />} />
                <Route path="/link-script/partnerships/:id" element={<SecuredComponent element={<LegacyPartnershipRedirect />} />} />
                <Route path="/link-script/partnerships/:id/print" element={<SecuredComponent element={<LegacyPartnershipRedirect print />} />} />
                <Route path="/partnerships" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/partnerships/:id" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/partnerships/:id/:tab" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/partnerships/:id/print" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/link-builder" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/alerts" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/network-settings" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/scheduled-reports" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/eb-pristjek" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/eb-pristjek/products" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/eb-pristjek/articles" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/eb-pristjek/rabatkoder" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/eb-pristjek/rabatkoder/:categoryId" element={<SecuredComponent element={<Dashboard />} />} />
                <Route path="/user-management" element={<AdminSecuredComponent element={<Dashboard />} />} />
                <Route path="/partnership-agreements" element={<AdminSecuredComponent element={<Dashboard />} />} />
                <Route path="/partnership-agreements/:id" element={<AdminSecuredComponent element={<Dashboard />} />} />
                <Route path="/conversions" element={<AdminSecuredComponent element={<Dashboard />} />} />
                <Route path="/publisher-campaign-urls" element={<AdminSecuredComponent element={<Dashboard />} />} />
                <Route path="/network-credentials/credentials" element={<AdminSecuredComponent element={<Dashboard />} />} />
                <Route path="/network-credentials/data-sync" element={<AdminSecuredComponent element={<Dashboard />} />} />
              </Route>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignupForm />} />
              <Route path="/alert-report" element={<AlertReport />} />
              <Route path="/user-settings" element={<SecuredComponent element={<UserSettings />} />} />
              <Route path="*" element={<NoMatch />} />
            </Routes>
        </AuthProvider>
      </AppProvider>
    </div>
  );
}

export default App;
